import { ehInvisivel } from './vo/texto.js';
import { eventoDaFonte, FONTES_MATON, problemaChamadaMaton, problemaCorpoMaton, RE_ID_MATON, type AppAcaoMaton, type FonteGatilhoMaton, type MetodoMaton } from './maton.js';

/**
 * U14 F1: o fluxo das automações em canvas (estilo ManyChat), igual no servidor e na tela.
 *
 * - **Desenho:** caixas (tipo, posição e configuração) e ligações (da saída de uma caixa para outra caixa).
 * - **Validação:** a mesma regra no editor (problemas na tela) e no servidor (publicar recusa desenho com problema).
 * - **Interpretador:** roda as caixas a partir de uma entrada (início, mensagem, clique, tempo) até uma caixa de espera,
 *   do fim ou do teto de passos. Puro: tudo o que sai do fluxo (enviar, etiqueta, transferir) passa pela
 *   `PortaFluxo`. O motor (módulo Automações) liga a porta à `conversas.mensageria`; o "Simular" da tela, a um registro
 *   que não envia nada.
 *
 * Sem dependência externa (o pacote é usado pelo web e pelos módulos).
 */

export const VERSAO_DESENHO = 1;
export const CANAIS_FLUXO = ['whatsapp', 'instagram', 'qualquer'] as const;
export type CanalFluxo = (typeof CANAIS_FLUXO)[number];
export type CanalMensagem = 'whatsapp' | 'instagram';

export const TIPOS_CAIXA = [
  'gatilho_palavra',
  'gatilho_comentario',
  'gatilho_broadcast',
  'gatilho_app',
  'mensagem',
  'botoes',
  'pergunta',
  'resposta_publica',
  'resposta_privada',
  'condicao',
  'espera',
  'etiqueta',
  'transferir',
  'oportunidade',
  'webhook',
  'app_externo',
  'fim',
] as const;
export type TipoCaixa = (typeof TIPOS_CAIXA)[number];
export const GRUPO_CAIXA: Record<TipoCaixa, 'gatilho' | 'mensagem' | 'logica' | 'acao'> = {
  gatilho_palavra: 'gatilho',
  gatilho_comentario: 'gatilho',
  gatilho_broadcast: 'gatilho',
  gatilho_app: 'gatilho',
  mensagem: 'mensagem',
  resposta_publica: 'mensagem',
  resposta_privada: 'mensagem',
  botoes: 'mensagem',
  pergunta: 'mensagem',
  condicao: 'logica',
  espera: 'logica',
  fim: 'logica',
  etiqueta: 'acao',
  transferir: 'acao',
  oportunidade: 'acao',
  webhook: 'acao',
  app_externo: 'acao',
};

/**
 * U14 F2: caixas que precisam da conversa (Direct ou WhatsApp). No fluxo de comentário, a conversa só existe depois da
 * resposta privada; antes dela, só resposta pública, lógica, webhook e fim.
 */
export const PRECISA_CONVERSA: ReadonlySet<TipoCaixa> = new Set<TipoCaixa>(['mensagem', 'botoes', 'pergunta', 'etiqueta', 'transferir', 'oportunidade']);

export const MODOS_PALAVRA = ['exata', 'contem', 'palavra'] as const;
export type ModoPalavra = (typeof MODOS_PALAVRA)[number];
export const FORMATOS_PERGUNTA = ['texto', 'numero', 'email', 'telefone'] as const;
export type FormatoPergunta = (typeof FORMATOS_PERGUNTA)[number];
export const UNIDADES_ESPERA = ['minutos', 'horas', 'dias'] as const;
export type UnidadeEspera = (typeof UNIDADES_ESPERA)[number];
export const OPERADORES_VARIAVEL = ['igual', 'diferente', 'contem', 'existe', 'nao_existe', 'maior', 'menor'] as const;
export type OperadorVariavel = (typeof OPERADORES_VARIAVEL)[number];

export interface ConfigGatilhoPalavra {
  canal: CanalFluxo;
  palavras: string[];
  modo: ModoPalavra;
}
/** U14 F2: o post escolhido (id da mídia na Meta; legenda, link e miniatura só para a tela). */
export interface PostInstagram {
  id: string;
  legenda: string;
  link: string | null;
  miniatura: string | null;
}
/** U14 F2: comentário em post do Instagram; `post` nulo = qualquer post; `filtro` `todos` = qualquer comentário. */
export interface ConfigGatilhoComentario {
  post: PostInstagram | null;
  filtro: 'todos' | 'palavras';
  palavras: string[];
  modo: ModoPalavra;
}
/**
 * U14 F5: clique num botão de broadcast. O disparo escolhe, botão a botão, o fluxo que começa; o fluxo só declara que nasce do
 * clique (WhatsApp, na conversa do contato que tocou).
 */
export type ConfigGatilhoBroadcast = Record<string, never>;
/**
 * U14 F6: evento de app externo entregue pela Maton (`app` e `evento` da lista da Maton; `conexao_id`, a conta do app na
 * Maton; `gatilho_id`, o gatilho criado na Maton pelo "Criar na Maton", que aponta o webhook do workspace). O fluxo roda sem
 * conversa: só lógica, webhook e ação em app externo.
 */
export interface ConfigGatilhoApp {
  app: FonteGatilhoMaton | '';
  evento: string;
  conexao_id: string | null;
  gatilho_id: string | null;
}
export interface MidiaCaixa {
  /** Anexo do fluxo (anexos do núcleo, entidade `automacoes.fluxo`). */
  anexo_id: string;
  nome: string;
  mime: string;
}
export interface ConfigMensagem {
  texto: string;
  midia?: MidiaCaixa | null;
}
export interface OpcaoBotao {
  /** Também é o nome da saída da caixa. */
  id: string;
  titulo: string;
}
export interface ConfigBotoes {
  texto: string;
  modo: 'botoes' | 'respostas_rapidas';
  opcoes: OpcaoBotao[];
  /** Com prazo, a caixa ganha a saída `sem_resposta`. */
  prazo_horas?: number | null;
}
/** U14 F2: resposta pública embaixo do comentário (uma por comentário). */
export interface ConfigRespostaPublica {
  texto: string;
}
/**
 * U14 F2: resposta privada (Direct a partir do comentário, uma por comentário, até 7 dias). Com opções, a caixa espera o
 * clique (uma saída por opção, prazo opcional com `sem_resposta`); sem opções, segue por `proximo`.
 */
export interface ConfigRespostaPrivada {
  texto: string;
  modo: 'botoes' | 'respostas_rapidas';
  opcoes: OpcaoBotao[];
  prazo_horas?: number | null;
}
/** U14 F2: abre uma oportunidade no Pipeline para o contato da conversa (uma aberta por conversa). */
export interface ConfigOportunidade {
  titulo: string;
  /** Funil pelo id ou pelo nome; nulo = o padrão. */
  funil: string | null;
}
export interface ConfigPergunta {
  texto: string;
  variavel: string;
  formato: FormatoPergunta;
  mensagem_invalida?: string | null;
  prazo_horas?: number | null;
}
export type RegraCondicao =
  | { campo: 'variavel'; variavel: string; operador: OperadorVariavel; valor?: string }
  | { campo: 'etiqueta'; operador: 'tem' | 'nao_tem'; etiqueta: string }
  | { campo: 'horario'; dias: number[]; inicio: string; fim: string }
  | { campo: 'canal'; valor: CanalMensagem }
  /** U14 F2: o contato segue a conta do Instagram (perfil do IGSID na Meta); sem resposta da Meta, a regra não vale. */
  | { campo: 'segue'; operador: 'segue' | 'nao_segue' };
export interface ConfigCondicao {
  juncao: 'todas' | 'alguma';
  regras: RegraCondicao[];
}
export interface ConfigEspera {
  quantidade: number;
  unidade: UnidadeEspera;
}
export interface ConfigEtiqueta {
  acao: 'por' | 'tirar';
  etiqueta: string;
}
export interface ConfigTransferir {
  /** Fila do Conversas; nula = a fila padrão do canal. */
  fila_id: string | null;
  mensagem?: string | null;
}
export interface ConfigWebhook {
  url: string;
}
/**
 * U14 F6: chamada à API de um app pela Maton (`https://api.maton.ai/{app}{caminho}`, com a conta `conexao_id`). Só as
 * chamadas da lista (`OPERACOES_MATON`); as variáveis entram no caminho e no corpo; a resposta vai para `variavel`.
 */
export interface ConfigAppExterno {
  app: AppAcaoMaton | '';
  conexao_id: string;
  metodo: MetodoMaton;
  caminho: string;
  corpo: string;
  variavel: string | null;
}
export type ConfigFim = Record<string, never>;

export interface ConfigPorTipo {
  gatilho_palavra: ConfigGatilhoPalavra;
  gatilho_comentario: ConfigGatilhoComentario;
  gatilho_broadcast: ConfigGatilhoBroadcast;
  mensagem: ConfigMensagem;
  botoes: ConfigBotoes;
  pergunta: ConfigPergunta;
  resposta_publica: ConfigRespostaPublica;
  resposta_privada: ConfigRespostaPrivada;
  condicao: ConfigCondicao;
  espera: ConfigEspera;
  etiqueta: ConfigEtiqueta;
  transferir: ConfigTransferir;
  oportunidade: ConfigOportunidade;
  webhook: ConfigWebhook;
  gatilho_app: ConfigGatilhoApp;
  app_externo: ConfigAppExterno;
  fim: ConfigFim;
}

export type Caixa = { [T in TipoCaixa]: { id: string; tipo: T; x: number; y: number; config: ConfigPorTipo[T] } }[TipoCaixa];
export type CaixaDe<T extends TipoCaixa> = Extract<Caixa, { tipo: T }>;

export interface Ligacao {
  id: string;
  de: string;
  saida: string;
  para: string;
}

export interface Desenho {
  versao: number;
  caixas: Caixa[];
  ligacoes: Ligacao[];
}

export const LIMITES_FLUXO = {
  caixas: 200,
  ligacoes: 400,
  texto: 1024,
  palavras: 20,
  palavra: 60,
  opcoesBotoes: 3,
  opcoesRapidas: 10,
  tituloOpcao: 20,
  regras: 10,
  etiqueta: 30,
  esperaDias: 30,
  prazoHoras: 24 * 7,
  /** Passos numa rodada do interpretador (contra laço entre caixas sem espera). */
  passosRodada: 60,
  /** Passos na execução inteira. */
  passosExecucao: 500,
  variaveis: 50,
  valorVariavel: 1000,
  /** Texto acima dos botões no Instagram (modelo de botões da Meta). */
  textoBotoesInstagram: 640,
  /** U14 F2: respostas rápidas no Direct do Instagram (resposta privada). */
  opcoesRapidasInstagram: 13,
  /** U14 F2: Direct do Instagram sem botões. */
  textoInstagram: 1000,
  tituloOportunidade: 200,
  funil: 120,
  /** U14 F2: respostas privadas por hora por conta do Instagram (regra da Meta). */
  privadasPorHora: 750,
} as const;

export const TIPOS_GATILHO = ['gatilho_palavra', 'gatilho_comentario', 'gatilho_app', 'gatilho_broadcast'] as const;
export type TipoGatilho = (typeof TIPOS_GATILHO)[number];

/** Desenho vazio de um fluxo novo: só o gatilho (U14 F2: de palavra-chave ou de comentário em post). */
export function desenhoInicial(canal: CanalFluxo = 'qualquer', gatilho: TipoGatilho = 'gatilho_palavra'): Desenho {
  const caixa: Caixa =
    gatilho === 'gatilho_comentario'
      ? { id: 'gatilho', tipo: 'gatilho_comentario', x: 0, y: 0, config: configPadrao('gatilho_comentario') }
      : gatilho === 'gatilho_broadcast'
        ? { id: 'gatilho', tipo: 'gatilho_broadcast', x: 0, y: 0, config: {} }
        : gatilho === 'gatilho_app'
          ? { id: 'gatilho', tipo: 'gatilho_app', x: 0, y: 0, config: configPadrao('gatilho_app') }
        : { id: 'gatilho', tipo: 'gatilho_palavra', x: 0, y: 0, config: { canal, palavras: [], modo: 'palavra' } };
  return { versao: VERSAO_DESENHO, caixas: [caixa], ligacoes: [] };
}

/** U14 F2: canal do fluxo pelo gatilho (o de comentário é sempre do Instagram). */
export function canalDoGatilho(d: Desenho): CanalFluxo | null {
  const g = Array.isArray(d?.caixas) ? d.caixas.find((c) => GRUPO_CAIXA[c.tipo] === 'gatilho') : undefined;
  if (!g) return null;
  if (g.tipo === 'gatilho_comentario') return 'instagram';
  // U14 F5: o broadcast é só do WhatsApp (template aprovado).
  if (g.tipo === 'gatilho_broadcast') return 'whatsapp';
  // U14 F6: o evento de app externo não tem canal (roda sem conversa).
  if (g.tipo === 'gatilho_app') return 'qualquer';
  return g.tipo === 'gatilho_palavra' && CANAIS_FLUXO.includes(g.config.canal) ? g.config.canal : null;
}

/** Configuração padrão da caixa nova no editor. */
export function configPadrao<T extends TipoCaixa>(tipo: T): ConfigPorTipo[T] {
  const padroes: ConfigPorTipo = {
    gatilho_palavra: { canal: 'qualquer', palavras: [], modo: 'palavra' },
    gatilho_comentario: { post: null, filtro: 'todos', palavras: [], modo: 'palavra' },
    gatilho_broadcast: {},
    mensagem: { texto: '' },
    botoes: { texto: '', modo: 'botoes', opcoes: [{ id: 'op1', titulo: '' }], prazo_horas: null },
    pergunta: { texto: '', variavel: 'resposta', formato: 'texto', mensagem_invalida: null, prazo_horas: null },
    resposta_publica: { texto: '' },
    resposta_privada: { texto: '', modo: 'botoes', opcoes: [], prazo_horas: null },
    oportunidade: { titulo: 'Instagram: {{usuario}}', funil: null },
    condicao: { juncao: 'todas', regras: [{ campo: 'variavel', variavel: '', operador: 'existe' }] },
    espera: { quantidade: 1, unidade: 'horas' },
    etiqueta: { acao: 'por', etiqueta: '' },
    transferir: { fila_id: null, mensagem: null },
    webhook: { url: '' },
    gatilho_app: { app: '', evento: '', conexao_id: null, gatilho_id: null },
    app_externo: { app: '', conexao_id: '', metodo: 'POST', caminho: '', corpo: '', variavel: 'resposta_app' },
    fim: {},
  };
  return padroes[tipo];
}

/** Saídas de uma caixa (ponto de ligação). Botões: uma por opção; prazo: `sem_resposta`. */
export function saidasDaCaixa(c: Caixa): string[] {
  switch (c.tipo) {
    case 'gatilho_palavra':
    case 'gatilho_comentario':
    case 'gatilho_broadcast':
    case 'gatilho_app':
    case 'mensagem':
    case 'resposta_publica':
    case 'espera':
    case 'etiqueta':
    case 'oportunidade':
      return ['proximo'];
    case 'botoes':
      return [...c.config.opcoes.map((o) => o.id), ...(c.config.prazo_horas ? ['sem_resposta'] : [])];
    case 'resposta_privada':
      return Array.isArray(c.config.opcoes) && c.config.opcoes.length ? [...c.config.opcoes.map((o) => o.id), ...(c.config.prazo_horas ? ['sem_resposta'] : [])] : ['proximo'];
    case 'pergunta':
      return ['respondeu', ...(c.config.prazo_horas ? ['sem_resposta'] : [])];
    case 'condicao':
      return ['sim', 'nao'];
    case 'webhook':
    case 'app_externo':
      return ['ok', 'erro'];
    case 'transferir':
    case 'fim':
      return [];
  }
}

// ---------------------------------------------------------------------------
// Texto e palavras
// ---------------------------------------------------------------------------

/** Sem acento, minúsculas, espaços simples (a mesma busca de Conversas > Automação). */
export function normalizarPalavra(t: string): string {
  return t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/\s+/g, ' ')
    .trim();
}

const escaparRegex = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** O texto casa com alguma das palavras no modo: exata (o texto inteiro), contém (em qualquer lugar) ou palavra inteira. */
export function textoCasa(texto: string, palavras: readonly string[], modo: ModoPalavra): boolean {
  const t = normalizarPalavra(texto);
  if (!t) return false;
  return palavras.some((bruta) => {
    const p = normalizarPalavra(bruta);
    if (!p) return false;
    if (modo === 'exata') return t === p;
    if (modo === 'contem') return t.includes(p);
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escaparRegex(p)}($|[^\\p{L}\\p{N}])`, 'u').test(t);
  });
}

/**
 * U14 F2 (review F2-r1, SEG-B1): link e miniatura do post só da Meta (o link do post no Instagram; a miniatura nas CDNs dela),
 * conferidos no servidor: a tela usa os dois em `<a href>` e `<img src>`.
 */
export function linkDePostValido(v: unknown): v is string {
  return typeof v === 'string' && v.length <= 2000 && /^https:\/\/(www\.)?instagram\.com\/\S+$/.test(v);
}
export function miniaturaDePostValida(v: unknown): v is string {
  if (typeof v !== 'string' || v.length > 2000 || !/^https:\/\/\S+$/.test(v)) return false;
  try {
    const h = new URL(v).hostname;
    return /(^|\.)cdninstagram\.com$/.test(h) || /(^|\.)fbcdn\.net$/.test(h);
  } catch {
    return false;
  }
}

/** U14 F2: o comentário casa com o gatilho (o post e o filtro de palavras; `todos` aceita qualquer texto). */
export function comentarioCasa(k: ConfigGatilhoComentario, midiaId: string | null, texto: string): boolean {
  if (k.post && k.post.id !== midiaId) return false;
  return k.filtro === 'todos' || textoCasa(texto, k.palavras, k.modo);
}

/** O canal da conversa serve ao canal do gatilho. */
export function canalServe(gatilho: CanalFluxo, canal: string): boolean {
  return gatilho === 'qualquer' ? canal === 'whatsapp' || canal === 'instagram' : gatilho === canal;
}

/** Especificidade do modo (o gatilho mais específico ganha quando vários casam): exata > palavra > contém. */
export const PESO_MODO: Record<ModoPalavra, number> = { exata: 3, palavra: 2, contem: 1 };

const RE_VARIAVEL_NOME = /^[a-z][a-z0-9_]{0,39}$/;
/** Troca `{{variavel}}` pelo valor (vazio quando não existe). */
export function preencherTexto(texto: string, variaveis: Readonly<Record<string, string>>): string {
  return texto.replace(/\{\{\s*([a-z][a-z0-9_]{0,39})\s*\}\}/g, (_m, nome: string) => variaveis[nome] ?? '');
}

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Resposta da pergunta no formato pedido; devolve o valor guardado ou nulo (inválida). */
export function respostaValida(texto: string, formato: FormatoPergunta): string | null {
  const t = texto.trim();
  if (!t) return null;
  if (formato === 'texto') return t.slice(0, LIMITES_FLUXO.valorVariavel);
  if (formato === 'numero') {
    const n = Number(t.replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));
    return Number.isFinite(n) ? String(n) : null;
  }
  if (formato === 'email') return RE_EMAIL.test(t) && t.length <= 254 ? t.toLowerCase() : null;
  const digitos = t.replace(/\D/g, '');
  return digitos.length >= 10 && digitos.length <= 13 ? digitos : null;
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

export interface ProblemaFluxo {
  /** Caixa com o problema (nulo: o fluxo inteiro). */
  caixa: string | null;
  codigo: string;
  mensagem: string;
}

const RE_ID = /^[A-Za-z0-9_-]{1,40}$/;
const RE_HORA = /^([01]\d|2[0-3]):[0-5]\d$/;
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const textoOk = (v: unknown, max: number, vazio = false): v is string => typeof v === 'string' && v.length <= max && (vazio || v.trim().length > 0);
const prazoOk = (v: unknown) => v === null || v === undefined || (typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= LIMITES_FLUXO.prazoHoras);
/** Algum caractere invisível ou de controle (a mesma regra dos nomes do sistema). */
const invisivel = { test: (t: string) => [...t].some(ehInvisivel) };

/** Problema: o código (traduzido na tela, `erros.<codigo>`) e o texto em pt-BR. */
type Problema = [codigo: string, mensagem: string];

/** U14 F2: opções de botões ou respostas rápidas da resposta privada (0 a 3 botões ou até 13 respostas rápidas). */
function problemasDasOpcoesInstagram(k: ConfigRespostaPrivada): Problema[] {
  const p: Problema[] = [];
  if (!Array.isArray(k.opcoes)) return [['caixa.opcoes_vazias', 'Opções inválidas']];
  const max = k.modo === 'botoes' ? LIMITES_FLUXO.opcoesBotoes : LIMITES_FLUXO.opcoesRapidasInstagram;
  if (k.opcoes.length > max) p.push(k.modo === 'botoes' ? ['caixa.botoes_demais', `No máximo ${max} botões`] : ['caixa.respostas_demais_instagram', `No máximo ${max} respostas rápidas no Instagram`]);
  const ids = new Set<string>();
  const titulos = new Set<string>();
  for (const o of k.opcoes) {
    if (!ehObjeto(o) || !RE_ID.test(String(o.id)) || o.id === 'sem_resposta' || o.id === 'proximo' || ids.has(o.id)) {
      p.push(['caixa.opcao_id', 'Opção com identificador inválido ou repetido']);
      break;
    }
    ids.add(o.id);
    if (!textoOk(o.titulo, LIMITES_FLUXO.tituloOpcao)) {
      // Review F2-r1 (VIS-B2): o aviso sai uma vez, não uma por opção vazia.
      if (!p.some(([codigo]) => codigo === 'caixa.opcao_titulo')) p.push(['caixa.opcao_titulo', `Cada opção tem de 1 a ${LIMITES_FLUXO.tituloOpcao} caracteres`]);
    }
    else if (titulos.has(normalizarPalavra(o.titulo))) p.push(['caixa.opcao_repetida', 'Duas opções com o mesmo texto']);
    else titulos.add(normalizarPalavra(o.titulo));
  }
  return p;
}

const RE_ID_MIDIA = /^[0-9A-Za-z_]{1,120}$/;

/**
 * Problemas da configuração de uma caixa. `canal` é o do gatilho do fluxo: o Instagram não recebe arquivo pela automação e o
 * texto acima dos botões dele vai até 640 caracteres (review r65, SPEC-B2).
 */
function problemasDaConfig(c: Caixa, canal: CanalFluxo): Problema[] {
  const p: Problema[] = [];
  const cfg = c.config as unknown;
  if (!ehObjeto(cfg)) return [['caixa.config', 'Configuração da caixa inválida']];
  switch (c.tipo) {
    case 'gatilho_palavra': {
      const k = c.config;
      if (!CANAIS_FLUXO.includes(k.canal)) p.push(['caixa.canal', 'Escolha o canal do gatilho']);
      if (!MODOS_PALAVRA.includes(k.modo)) p.push(['caixa.modo_palavra', 'Escolha como a palavra-chave casa']);
      if (!Array.isArray(k.palavras) || !k.palavras.length) p.push(['caixa.palavras_vazias', 'Informe ao menos uma palavra-chave']);
      else if (k.palavras.length > LIMITES_FLUXO.palavras) p.push(['caixa.palavras_demais', `No máximo ${LIMITES_FLUXO.palavras} palavras-chave`]);
      else if (k.palavras.some((w) => !textoOk(w, LIMITES_FLUXO.palavra) || !normalizarPalavra(w))) p.push(['caixa.palavra_tamanho', `Cada palavra-chave tem de 1 a ${LIMITES_FLUXO.palavra} caracteres`]);
      break;
    }
    case 'gatilho_comentario': {
      const k = c.config;
      if (
        k.post !== null &&
        (!ehObjeto(k.post) ||
          !RE_ID_MIDIA.test(String(k.post.id)) ||
          typeof k.post.legenda !== 'string' ||
          k.post.legenda.length > 2200 ||
          (k.post.link !== null && !linkDePostValido(k.post.link)) ||
          (k.post.miniatura !== null && !miniaturaDePostValida(k.post.miniatura)))
      )
        p.push(['caixa.post', 'Escolha o post de novo na lista']);
      if (k.filtro !== 'todos' && k.filtro !== 'palavras') p.push(['caixa.filtro_comentario', 'Escolha se vale qualquer comentário ou só com palavras-chave']);
      if (!MODOS_PALAVRA.includes(k.modo)) p.push(['caixa.modo_palavra', 'Escolha como a palavra-chave casa']);
      if (!Array.isArray(k.palavras) || k.palavras.length > LIMITES_FLUXO.palavras) p.push(['caixa.palavras_demais', `No máximo ${LIMITES_FLUXO.palavras} palavras-chave`]);
      else if (k.filtro === 'palavras' && !k.palavras.length) p.push(['caixa.palavras_vazias', 'Informe ao menos uma palavra-chave']);
      else if (k.filtro === 'palavras' && k.palavras.some((w) => !textoOk(w, LIMITES_FLUXO.palavra) || !normalizarPalavra(w))) p.push(['caixa.palavra_tamanho', `Cada palavra-chave tem de 1 a ${LIMITES_FLUXO.palavra} caracteres`]);
      break;
    }
    case 'resposta_publica':
      if (!textoOk(c.config.texto, LIMITES_FLUXO.texto)) p.push(['caixa.texto', `Escreva a resposta pública (até ${LIMITES_FLUXO.texto} caracteres)`]);
      break;
    case 'resposta_privada': {
      const k = c.config;
      if (k.modo !== 'botoes' && k.modo !== 'respostas_rapidas') p.push(['caixa.modo_botoes', 'Escolha botões ou respostas rápidas']);
      const comBotoes = Array.isArray(k.opcoes) && k.opcoes.length > 0 && k.modo === 'botoes';
      const maxTexto = comBotoes ? LIMITES_FLUXO.textoBotoesInstagram : LIMITES_FLUXO.textoInstagram;
      // Review F2-r1 (VIS-B1): texto vazio pede o texto; o limite dos botões só quando passa dele.
      if (!textoOk(k.texto, maxTexto)) {
        p.push(comBotoes && String(k.texto ?? '').trim() ? ['caixa.texto_instagram', `No Instagram, o texto acima dos botões vai até ${maxTexto} caracteres`] : ['caixa.texto_direct', `Escreva a resposta privada (até ${maxTexto} caracteres)`]);
      }
      p.push(...problemasDasOpcoesInstagram(k));
      if (!prazoOk(k.prazo_horas)) p.push(['caixa.prazo', `Prazo de 1 a ${LIMITES_FLUXO.prazoHoras} horas`]);
      break;
    }
    case 'oportunidade': {
      const k = c.config;
      if (!textoOk(k.titulo, LIMITES_FLUXO.tituloOportunidade) || invisivel.test(k.titulo)) p.push(['caixa.titulo_oportunidade', `Título da oportunidade de 1 a ${LIMITES_FLUXO.tituloOportunidade} caracteres`]);
      if (k.funil !== null && k.funil !== undefined && (!textoOk(k.funil, LIMITES_FLUXO.funil) || invisivel.test(k.funil))) p.push(['caixa.funil', `Funil de 1 a ${LIMITES_FLUXO.funil} caracteres`]);
      break;
    }
    case 'mensagem': {
      const k = c.config;
      const temMidia = !!k.midia;
      if (!textoOk(k.texto, LIMITES_FLUXO.texto, temMidia)) p.push(['caixa.texto', `Escreva a mensagem (até ${LIMITES_FLUXO.texto} caracteres)`]);
      if (temMidia && (!ehObjeto(k.midia) || !RE_UUID.test(String(k.midia.anexo_id)) || !textoOk(k.midia.nome, 200) || !textoOk(k.midia.mime, 200))) p.push(['caixa.arquivo_invalido', 'Arquivo da mensagem inválido']);
      else if (temMidia && canal !== 'whatsapp') p.push(['caixa.arquivo_canal', 'Arquivo pela automação só sai no WhatsApp: deixe o gatilho só no WhatsApp ou tire o arquivo']);
      break;
    }
    case 'botoes': {
      const k = c.config;
      if (!textoOk(k.texto, LIMITES_FLUXO.texto)) p.push(['caixa.texto', `Escreva o texto acima dos botões (até ${LIMITES_FLUXO.texto} caracteres)`]);
      else if (canal !== 'whatsapp' && k.texto.length > LIMITES_FLUXO.textoBotoesInstagram) p.push(['caixa.texto_instagram', `No Instagram, o texto acima dos botões vai até ${LIMITES_FLUXO.textoBotoesInstagram} caracteres`]);
      if (k.modo !== 'botoes' && k.modo !== 'respostas_rapidas') p.push(['caixa.modo_botoes', 'Escolha botões ou respostas rápidas']);
      const max = k.modo === 'botoes' ? LIMITES_FLUXO.opcoesBotoes : LIMITES_FLUXO.opcoesRapidas;
      if (!Array.isArray(k.opcoes) || !k.opcoes.length) p.push(['caixa.opcoes_vazias', 'Inclua ao menos uma opção']);
      else {
        if (k.opcoes.length > max) p.push(k.modo === 'botoes' ? ['caixa.botoes_demais', `No máximo ${max} botões`] : ['caixa.respostas_demais', `No máximo ${max} respostas rápidas`]);
        const ids = new Set<string>();
        const titulos = new Set<string>();
        for (const o of k.opcoes) {
          if (!ehObjeto(o) || !RE_ID.test(String(o.id)) || o.id === 'sem_resposta' || ids.has(o.id)) {
            p.push(['caixa.opcao_id', 'Opção com identificador inválido ou repetido']);
            break;
          }
          ids.add(o.id);
          if (!textoOk(o.titulo, LIMITES_FLUXO.tituloOpcao)) {
            if (!p.some(([codigo]) => codigo === 'caixa.opcao_titulo')) p.push(['caixa.opcao_titulo', `Cada opção tem de 1 a ${LIMITES_FLUXO.tituloOpcao} caracteres`]);
          }
          else if (titulos.has(normalizarPalavra(o.titulo))) p.push(['caixa.opcao_repetida', 'Duas opções com o mesmo texto']);
          else titulos.add(normalizarPalavra(o.titulo));
        }
      }
      if (!prazoOk(k.prazo_horas)) p.push(['caixa.prazo', `Prazo de 1 a ${LIMITES_FLUXO.prazoHoras} horas`]);
      break;
    }
    case 'pergunta': {
      const k = c.config;
      if (!textoOk(k.texto, LIMITES_FLUXO.texto)) p.push(['caixa.texto', `Escreva a pergunta (até ${LIMITES_FLUXO.texto} caracteres)`]);
      if (typeof k.variavel !== 'string' || !RE_VARIAVEL_NOME.test(k.variavel)) p.push(['caixa.variavel_nome', 'Nome da variável: letras minúsculas, números e _, começando por letra']);
      if (!FORMATOS_PERGUNTA.includes(k.formato)) p.push(['caixa.formato', 'Escolha o formato da resposta']);
      if (k.mensagem_invalida !== null && k.mensagem_invalida !== undefined && !textoOk(k.mensagem_invalida, LIMITES_FLUXO.texto, true)) p.push(['caixa.mensagem_longa', 'Mensagem de resposta inválida longa demais']);
      if (!prazoOk(k.prazo_horas)) p.push(['caixa.prazo', `Prazo de 1 a ${LIMITES_FLUXO.prazoHoras} horas`]);
      break;
    }
    case 'condicao': {
      const k = c.config;
      if (k.juncao !== 'todas' && k.juncao !== 'alguma') p.push(['caixa.juncao', 'Escolha se valem todas as regras ou alguma']);
      if (!Array.isArray(k.regras) || !k.regras.length) p.push(['caixa.regras_vazias', 'Inclua ao menos uma regra']);
      else if (k.regras.length > LIMITES_FLUXO.regras) p.push(['caixa.regras_demais', `No máximo ${LIMITES_FLUXO.regras} regras`]);
      else
        for (const r of k.regras) {
          const e = problemaDaRegra(r, canal);
          if (e) {
            p.push(e);
            break;
          }
        }
      break;
    }
    case 'espera': {
      const k = c.config;
      const max = { minutos: LIMITES_FLUXO.esperaDias * 1440, horas: LIMITES_FLUXO.esperaDias * 24, dias: LIMITES_FLUXO.esperaDias }[k.unidade];
      if (!UNIDADES_ESPERA.includes(k.unidade)) p.push(['caixa.unidade', 'Escolha a unidade da espera']);
      else if (!Number.isInteger(k.quantidade) || k.quantidade < 1 || k.quantidade > max) p.push(['caixa.espera_limite', `A espera vai de 1 minuto a ${LIMITES_FLUXO.esperaDias} dias`]);
      break;
    }
    case 'etiqueta': {
      const k = c.config;
      if (k.acao !== 'por' && k.acao !== 'tirar') p.push(['caixa.acao_etiqueta', 'Escolha pôr ou tirar a etiqueta']);
      if (!textoOk(k.etiqueta, LIMITES_FLUXO.etiqueta) || invisivel.test(k.etiqueta)) p.push(['caixa.etiqueta', `Etiqueta de 1 a ${LIMITES_FLUXO.etiqueta} caracteres`]);
      break;
    }
    case 'transferir': {
      const k = c.config;
      if (k.fila_id !== null && !RE_UUID.test(String(k.fila_id))) p.push(['caixa.fila', 'Fila inválida']);
      if (k.mensagem !== null && k.mensagem !== undefined && !textoOk(k.mensagem, LIMITES_FLUXO.texto, true)) p.push(['caixa.mensagem_longa', 'Mensagem longa demais']);
      break;
    }
    case 'webhook': {
      const k = c.config;
      let u: URL | null = null;
      try {
        u = new URL(String(k.url));
      } catch {
        u = null;
      }
      if (!u || u.protocol !== 'https:' || u.username || u.password || String(k.url).length > 500) p.push(['caixa.url', 'Endereço do webhook: https://, sem usuário e senha, até 500 caracteres']);
      break;
    }
    case 'gatilho_app': {
      // U14 F6: fonte e evento da lista da Maton; a conexão (conta do app) e o gatilho criado na Maton.
      const k = c.config;
      if (!FONTES_MATON.includes(k.app as FonteGatilhoMaton)) p.push(['caixa.fonte_maton', 'Escolha o app do evento']);
      else if (!eventoDaFonte(k.app, String(k.evento))) p.push(['caixa.evento_maton', 'Escolha o evento do app']);
      if (k.conexao_id === null || k.conexao_id === undefined || !RE_ID_MATON.test(String(k.conexao_id))) p.push(['caixa.conexao_maton', 'Escolha a conta do app (conexão da Maton)']);
      if (k.gatilho_id === null || k.gatilho_id === undefined || !RE_ID_MATON.test(String(k.gatilho_id))) p.push(['caixa.gatilho_maton', 'Crie o gatilho na Maton (botão "Criar na Maton")']);
      break;
    }
    case 'app_externo': {
      const k = c.config;
      const chamada = problemaChamadaMaton(String(k.app), String(k.metodo), String(k.caminho ?? ''));
      if (chamada) p.push(chamada);
      else {
        const corpo = problemaCorpoMaton(k.metodo, typeof k.corpo === 'string' ? k.corpo : '');
        if (corpo) p.push(corpo);
      }
      if (typeof k.conexao_id !== 'string' || !RE_ID_MATON.test(k.conexao_id)) p.push(['caixa.conexao_maton', 'Escolha a conta do app (conexão da Maton)']);
      if (k.variavel !== null && k.variavel !== undefined && k.variavel !== '' && (typeof k.variavel !== 'string' || !RE_VARIAVEL_NOME.test(k.variavel))) {
        p.push(['caixa.variavel_nome', 'Nome da variável: letras minúsculas, números e _, começando por letra']);
      }
      break;
    }
    case 'fim':
      break;
  }
  return p;
}

function problemaDaRegra(r: RegraCondicao, canal: CanalFluxo): Problema | null {
  if (!ehObjeto(r)) return ['caixa.regra_invalida', 'Regra inválida'];
  if (r.campo === 'segue') {
    if (r.operador !== 'segue' && r.operador !== 'nao_segue') return ['caixa.regra_comparacao', 'Escolha a comparação da regra'];
    return canal === 'instagram' ? null : ['caixa.regra_segue_canal', '"Segue a conta" só vale no Instagram: deixe o gatilho só no Instagram'];
  }
  if (r.campo === 'variavel') {
    if (!RE_VARIAVEL_NOME.test(String(r.variavel))) return ['caixa.regra_variavel', 'Escolha a variável da regra'];
    if (!OPERADORES_VARIAVEL.includes(r.operador)) return ['caixa.regra_comparacao', 'Escolha a comparação da regra'];
    if (r.operador !== 'existe' && r.operador !== 'nao_existe' && !textoOk(r.valor, 200)) return ['caixa.regra_valor', 'Informe o valor da regra'];
    return null;
  }
  if (r.campo === 'etiqueta') return (r.operador === 'tem' || r.operador === 'nao_tem') && textoOk(r.etiqueta, LIMITES_FLUXO.etiqueta) ? null : ['caixa.regra_etiqueta', 'Informe a etiqueta da regra'];
  if (r.campo === 'horario') {
    if (!Array.isArray(r.dias) || !r.dias.length || r.dias.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return ['caixa.regra_dias', 'Escolha os dias da semana da regra'];
    if (!RE_HORA.test(String(r.inicio)) || !RE_HORA.test(String(r.fim)) || r.inicio >= r.fim) return ['caixa.regra_horario', 'Horário da regra: início antes do fim (HH:MM)'];
    return null;
  }
  if (r.campo === 'canal') return r.valor === 'whatsapp' || r.valor === 'instagram' ? null : ['caixa.regra_canal', 'Escolha o canal da regra'];
  return ['caixa.regra_invalida', 'Regra inválida'];
}

export function validarDesenho(d: Desenho): ProblemaFluxo[] {
  const p: ProblemaFluxo[] = [];
  if (!ehObjeto(d) || !Array.isArray(d.caixas) || !Array.isArray(d.ligacoes)) return [{ caixa: null, codigo: 'desenho.invalido', mensagem: 'Desenho inválido' }];
  if (d.caixas.length > LIMITES_FLUXO.caixas) p.push({ caixa: null, codigo: 'desenho.caixas', mensagem: `No máximo ${LIMITES_FLUXO.caixas} caixas` });
  if (d.ligacoes.length > LIMITES_FLUXO.ligacoes) p.push({ caixa: null, codigo: 'desenho.ligacoes', mensagem: `No máximo ${LIMITES_FLUXO.ligacoes} ligações` });
  const caixas = new Map<string, Caixa>();
  for (const c of d.caixas) {
    if (!ehObjeto(c) || !RE_ID.test(String(c.id)) || !TIPOS_CAIXA.includes(c.tipo)) {
      p.push({ caixa: null, codigo: 'caixa.invalida', mensagem: 'Caixa com identificador ou tipo inválido' });
      continue;
    }
    if (caixas.has(c.id)) {
      p.push({ caixa: c.id, codigo: 'caixa.repetida', mensagem: 'Caixa repetida' });
      continue;
    }
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y)) p.push({ caixa: c.id, codigo: 'caixa.posicao', mensagem: 'Posição da caixa inválida' });
    caixas.set(c.id, c);
  }
  const gatilhos = [...caixas.values()].filter((c) => GRUPO_CAIXA[c.tipo] === 'gatilho');
  // U14 F2: o fluxo de comentário é do Instagram (arquivo e texto dos botões seguem a regra dele).
  const canal: CanalFluxo = canalDoGatilho({ versao: d.versao, caixas: [...caixas.values()], ligacoes: [] }) ?? 'qualquer';
  for (const c of caixas.values()) for (const [codigo, mensagem] of problemasDaConfig(c, canal)) p.push({ caixa: c.id, codigo, mensagem });
  if (gatilhos.length !== 1) p.push({ caixa: null, codigo: 'desenho.gatilho', mensagem: gatilhos.length ? 'O fluxo tem um gatilho só' : 'Inclua o gatilho do fluxo' });

  const ocupadas = new Set<string>();
  const ids = new Set<string>();
  const seguintes = new Map<string, string[]>();
  for (const l of d.ligacoes) {
    if (!ehObjeto(l) || !RE_ID.test(String(l.id)) || ids.has(l.id)) {
      p.push({ caixa: null, codigo: 'ligacao.invalida', mensagem: 'Ligação com identificador inválido ou repetido' });
      continue;
    }
    ids.add(l.id);
    const de = caixas.get(l.de);
    const para = caixas.get(l.para);
    if (!de || !para) {
      p.push({ caixa: de?.id ?? null, codigo: 'ligacao.caixa', mensagem: 'Ligação para uma caixa que não existe' });
      continue;
    }
    if (!saidasDaCaixa(de).includes(l.saida)) {
      p.push({ caixa: de.id, codigo: 'ligacao.saida', mensagem: 'Ligação de uma saída que a caixa não tem' });
      continue;
    }
    if (GRUPO_CAIXA[para.tipo] === 'gatilho') {
      p.push({ caixa: de.id, codigo: 'ligacao.gatilho', mensagem: 'Nada liga de volta ao gatilho' });
      continue;
    }
    const chave = `${l.de}\u0000${l.saida}`;
    if (ocupadas.has(chave)) {
      p.push({ caixa: de.id, codigo: 'ligacao.repetida', mensagem: 'Cada saída liga a uma caixa só' });
      continue;
    }
    ocupadas.add(chave);
    seguintes.set(l.de, [...(seguintes.get(l.de) ?? []), l.para]);
  }

  const g = gatilhos[0];
  if (g) {
    if (!seguintes.get(g.id)?.length) p.push({ caixa: g.id, codigo: 'gatilho.solto', mensagem: 'Ligue o gatilho à primeira caixa' });
    const vistas = new Set([g.id]);
    const fila = [g.id];
    while (fila.length) {
      for (const s of seguintes.get(fila.shift()!) ?? []) {
        if (vistas.has(s)) continue;
        vistas.add(s);
        fila.push(s);
      }
    }
    for (const c of caixas.values()) if (!vistas.has(c.id)) p.push({ caixa: c.id, codigo: 'caixa.solta', mensagem: 'Caixa fora do caminho do gatilho' });
    p.push(...problemasDoComentario(g, caixas, seguintes, d.ligacoes));
    p.push(...problemasDoEventoApp(g, caixas));
  }
  // Review r65 (ROB-B1): laço sem caixa que espera o contato (botões, pergunta, espera) mandaria mensagens e chamaria o
  // webhook em rajada até o teto de passos. O laço precisa passar por uma delas.
  const esperam = new Set<TipoCaixa>(['botoes', 'pergunta', 'espera']);
  const cor = new Map<string, 1 | 2>();
  const noLaco = new Set<string>();
  const visitar = (id: string, pilha: string[]) => {
    cor.set(id, 1);
    pilha.push(id);
    for (const s of seguintes.get(id) ?? []) {
      const c = caixas.get(s);
      if (!c || esperam.has(c.tipo)) continue;
      if (cor.get(s) === 1) for (const x of pilha.slice(pilha.indexOf(s))) noLaco.add(x);
      else if (!cor.get(s)) visitar(s, pilha);
    }
    pilha.pop();
    cor.set(id, 2);
  };
  for (const c of caixas.values()) if (!esperam.has(c.tipo) && !cor.get(c.id)) visitar(c.id, []);
  for (const id of noLaco) p.push({ caixa: id, codigo: 'desenho.laco', mensagem: 'Laço sem espera: passe por botões, pergunta ou espera antes de voltar' });
  return p;
}

/**
 * U14 F2: regras do fluxo de comentário. Resposta pública e privada só com o gatilho de comentário; no caminho do gatilho
 * até a resposta privada, nada que precise da conversa (o Direct só abre com ela); uma resposta de cada tipo por caminho
 * (a Meta aceita uma pública e uma privada por comentário).
 */
function problemasDoComentario(g: Caixa, caixas: Map<string, Caixa>, seguintes: Map<string, string[]>, desenhoLigacoes: Ligacao[]): ProblemaFluxo[] {
  const p: ProblemaFluxo[] = [];
  if (g.tipo !== 'gatilho_comentario') {
    for (const c of caixas.values()) {
      if (c.tipo === 'resposta_publica' || c.tipo === 'resposta_privada') p.push({ caixa: c.id, codigo: 'caixa.so_comentario', mensagem: 'Resposta ao comentário só com o gatilho "Comentário em post"' });
    }
    return p;
  }
  const alcancaveis = (inicio: string[], para: (c: Caixa) => boolean) => {
    const vistas = new Set<string>();
    const fila = [...inicio];
    while (fila.length) {
      const id = fila.shift()!;
      if (vistas.has(id)) continue;
      vistas.add(id);
      const c = caixas.get(id);
      if (c && !para(c)) fila.push(...(seguintes.get(id) ?? []));
    }
    return vistas;
  };
  for (const id of alcancaveis(seguintes.get(g.id) ?? [], (c) => c.tipo === 'resposta_privada')) {
    const c = caixas.get(id);
    if (c && PRECISA_CONVERSA.has(c.tipo)) p.push({ caixa: c.id, codigo: 'caixa.antes_privada', mensagem: 'Ponha a resposta privada antes desta caixa: o Direct com quem comentou só abre com ela' });
  }
  // A Meta aceita outra mensagem no Direct aberto pela resposta privada só depois que a pessoa responde (clique ou texto).
  // Pelas saídas da privada que não são resposta da pessoa ("depois" sem opções, "sem resposta" com prazo), nada que mande
  // mensagem no Direct até uma caixa que espera a resposta, e essa também não pode mandar.
  const enviaNoDirect = new Set<TipoCaixa>(['mensagem', 'botoes', 'pergunta']);
  for (const c of caixas.values()) {
    if (c.tipo !== 'resposta_privada') continue;
    const saidas = c.config.opcoes?.length ? ['sem_resposta'] : ['proximo'];
    const inicio = saidas.flatMap((sd) => desenhoLigacoes.filter((l) => l.de === c.id && l.saida === sd).map((l) => l.para));
    for (const id of alcancaveis(inicio, (x) => enviaNoDirect.has(x.tipo) || x.tipo === 'resposta_privada')) {
      const x = caixas.get(id);
      if (x && enviaNoDirect.has(x.tipo)) p.push({ caixa: x.id, codigo: 'caixa.depois_privada', mensagem: 'O Direct só aceita outra mensagem depois que a pessoa responde: ponha botões na resposta privada e siga pelo clique' });
    }
  }
  for (const tipo of ['resposta_publica', 'resposta_privada'] as const) {
    const repetidas = new Set<string>();
    for (const c of caixas.values()) {
      if (c.tipo !== tipo) continue;
      for (const id of alcancaveis(seguintes.get(c.id) ?? [], () => false)) if (caixas.get(id)?.tipo === tipo) repetidas.add(id);
    }
    for (const id of repetidas) {
      p.push({ caixa: id, codigo: 'caixa.resposta_repetida', mensagem: tipo === 'resposta_publica' ? 'Uma resposta pública por comentário: esta vem depois de outra' : 'Uma resposta privada por comentário: esta vem depois de outra' });
    }
  }
  return p;
}

/**
 * U14 F6: o fluxo do evento de app externo roda sem conversa (não há contato para receber mensagem, etiqueta ou fila): só
 * condição, espera, fim, webhook e ação em app externo.
 */
function problemasDoEventoApp(g: Caixa, caixas: Map<string, Caixa>): ProblemaFluxo[] {
  if (g.tipo !== 'gatilho_app') return [];
  const p: ProblemaFluxo[] = [];
  for (const c of caixas.values()) {
    if (PRECISA_CONVERSA.has(c.tipo)) p.push({ caixa: c.id, codigo: 'caixa.sem_conversa_app', mensagem: 'O evento de app externo não tem conversa: use só lógica, webhook e ação em app externo' });
  }
  return p;
}

// ---------------------------------------------------------------------------
// Interpretador
// ---------------------------------------------------------------------------

export type SituacaoExecucao = 'rodando' | 'aguardando' | 'concluida' | 'falhou' | 'cancelada';

export type Espera =
  | { tipo: 'tempo'; caixa: string; ate: string }
  /** Review r65 (ROB-M1): o POST do bloco webhook sai fora da transação, pelo motor, que devolve o resultado como entrada. */
  | { tipo: 'webhook'; caixa: string; ate: null }
  /** U14 F6: a chamada à Maton sai fora da transação, pelo motor (como o webhook). */
  | { tipo: 'app_externo'; caixa: string; ate: null }
  /** U14 F2: a caixa roda de novo no prazo (resposta privada no teto de 750 por hora da conta). */
  | { tipo: 'repetir'; caixa: string; ate: string }
  | { tipo: 'clique'; caixa: string; ate: string | null }
  | { tipo: 'resposta'; caixa: string; ate: string | null };

export interface EstadoExecucao {
  /** Caixa atual (a que espera) ou a última que rodou. */
  caixa: string | null;
  variaveis: Record<string, string>;
  espera: Espera | null;
  passos: number;
  situacao: SituacaoExecucao;
  motivo: string | null;
}

export type EntradaFluxo =
  | { tipo: 'inicio' }
  /** Início a partir de uma saída (o clique num botão de uma execução que já terminou). */
  | { tipo: 'retomar_saida'; caixa: string; saida: string }
  | { tipo: 'mensagem'; texto: string }
  /** Clique já resolvido pelo código do botão (o motor confere execução e caixa). */
  | { tipo: 'clique'; caixa: string; saida: string }
  /** Resultado do POST do bloco webhook (feito pelo motor fora da transação). */
  | { tipo: 'webhook'; caixa: string; ok: boolean; detalhe: string | null }
  /** U14 F6: resultado da chamada à Maton (feita pelo motor fora da transação) e as variáveis da resposta. */
  | { tipo: 'app_externo'; caixa: string; ok: boolean; detalhe: string | null; variaveis?: Record<string, string> }
  | { tipo: 'tempo' };

/** `tentar_em` (U14 F2): a recusa é temporária (teto por hora); a caixa espera e roda de novo nesse horário. */
export type ResultadoAcao = { ok: true; detalhe?: string } | { ok: false; erro: string; tentar_em?: string };

export interface EnvioFluxo {
  caixa: string;
  texto: string;
  midia?: MidiaCaixa | null;
  /** Botões ou respostas rápidas: a saída de cada opção vai com ela (o motor grava o código do clique). */
  botoes?: { modo: 'botoes' | 'respostas_rapidas'; opcoes: { saida: string; titulo: string }[] } | null;
}

export interface PortaFluxo {
  agora(): Date;
  /** Fuso do workspace (regra de horário). */
  fuso: string;
  canal: CanalMensagem | null;
  etiquetas(): Promise<string[]>;
  enviar(m: EnvioFluxo): Promise<ResultadoAcao>;
  etiqueta(acao: 'por' | 'tirar', etiqueta: string): Promise<ResultadoAcao>;
  transferir(filaId: string | null, mensagem: string | null): Promise<ResultadoAcao>;
  /** U14 F2: resposta pública embaixo do comentário que abriu a execução. */
  responderComentario(caixa: string, texto: string): Promise<ResultadoAcao>;
  /** U14 F2: resposta privada ao comentário (abre a conversa do Direct); com botões, o motor grava o código do clique. */
  respostaPrivada(m: EnvioFluxo): Promise<ResultadoAcao>;
  /** U14 F2: o contato segue a conta do Instagram (nulo: a Meta não respondeu ou não é Instagram). */
  segueConta(): Promise<boolean | null>;
  /** U14 F2: abre a oportunidade no Pipeline para o contato da conversa (`detalhe` com o código do resultado). */
  oportunidade(titulo: string, funil: string | null): Promise<ResultadoAcao>;
}

/**
 * Review r65 (VIS-B3): o que o interpretador grava no motivo da execução e no detalhe do passo é um código (a tela traduz,
 * `fluxos.motivos.<código>` e `fluxos.passos.<código>`); o detalhe com parâmetro vai como `<código>:<valor>` (a data da
 * espera em ISO, que a tela mostra no fuso). Erro de envio, de etiqueta ou de transferência segue com o texto da recusa.
 */
export const MOTIVOS = {
  semGatilho: 'sem_gatilho',
  caixaInexistente: 'caixa_inexistente',
  variaveisDemais: 'variaveis_demais',
  limitePassos: 'limite_passos',
  ligacaoGatilho: 'ligacao_gatilho',
  transferida: 'transferida',
} as const;
export const DETALHES = {
  esperaConcluida: 'espera_concluida',
  semResposta: 'sem_resposta',
  clique: 'clique',
  respostaOpcao: 'resposta_opcao',
  respostaInvalida: 'resposta_invalida',
  respostaGuardada: 'resposta_guardada',
  esperandoClique: 'esperando_clique',
  esperandoResposta: 'esperando_resposta',
  ate: 'ate',
  transferida: 'transferida',
  webhook: 'webhook',
  /** U14 F6: esperando a resposta do app externo (Maton). */
  appExterno: 'app_externo',
  /** U14 F2. */
  respostaPublica: 'resposta_publica',
  respostaPrivada: 'resposta_privada',
  oportunidadeAberta: 'oportunidade_aberta',
  oportunidadeExistente: 'oportunidade_existente',
  /** `limite_hora:<ISO>`: teto de respostas privadas por hora da conta; nova tentativa no horário. */
  limiteHora: 'limite_hora',
} as const;

export interface PassoFluxo {
  caixa: string;
  tipo: TipoCaixa;
  resultado: 'ok' | 'erro' | 'aguardando';
  saida: string | null;
  detalhe: string | null;
  duracao_ms: number;
}

export interface RodadaFluxo {
  estado: EstadoExecucao;
  passos: PassoFluxo[];
}

export function estadoInicial(variaveis: Record<string, string> = {}): EstadoExecucao {
  return { caixa: null, variaveis: { ...variaveis }, espera: null, passos: 0, situacao: 'rodando', motivo: null };
}

const MS_UNIDADE: Record<UnidadeEspera, number> = { minutos: 60_000, horas: 3_600_000, dias: 86_400_000 };

/** Hora local (dia da semana e HH:MM) no fuso. */
export function horaLocal(agora: Date, fuso: string): { dia: number; hora: string } {
  try {
    const partes = new Intl.DateTimeFormat('en-US', { timeZone: fuso, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(agora);
    const v = (t: string) => partes.find((x) => x.type === t)?.value ?? '';
    const dia = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(v('weekday'));
    return { dia, hora: `${v('hour').padStart(2, '0')}:${v('minute').padStart(2, '0')}` };
  } catch {
    return { dia: agora.getUTCDay(), hora: agora.toISOString().slice(11, 16) };
  }
}

function compararVariavel(valor: string | undefined, r: Extract<RegraCondicao, { campo: 'variavel' }>): boolean {
  const alvo = r.valor ?? '';
  switch (r.operador) {
    case 'existe':
      return valor !== undefined && valor !== '';
    case 'nao_existe':
      return valor === undefined || valor === '';
    case 'igual':
      return valor !== undefined && normalizarPalavra(valor) === normalizarPalavra(alvo);
    case 'diferente':
      return valor === undefined || normalizarPalavra(valor) !== normalizarPalavra(alvo);
    case 'contem':
      return valor !== undefined && normalizarPalavra(valor).includes(normalizarPalavra(alvo));
    case 'maior':
    case 'menor': {
      const a = Number(String(valor ?? '').replace(',', '.'));
      const b = Number(alvo.replace(',', '.'));
      if (!Number.isFinite(a) || !Number.isFinite(b) || valor === undefined || valor === '') return false;
      return r.operador === 'maior' ? a > b : a < b;
    }
  }
}

async function avaliarCondicao(k: ConfigCondicao, estado: EstadoExecucao, porta: PortaFluxo): Promise<boolean> {
  let etiquetas: string[] | null = null;
  const resultados: boolean[] = [];
  for (const r of k.regras) {
    let v = false;
    if (r.campo === 'variavel') v = compararVariavel(estado.variaveis[r.variavel], r);
    else if (r.campo === 'etiqueta') {
      etiquetas ??= (await porta.etiquetas()).map(normalizarPalavra);
      const tem = etiquetas.includes(normalizarPalavra(r.etiqueta));
      v = r.operador === 'tem' ? tem : !tem;
    } else if (r.campo === 'horario') {
      const { dia, hora } = horaLocal(porta.agora(), porta.fuso);
      v = r.dias.includes(dia) && hora >= r.inicio && hora < r.fim;
    } else if (r.campo === 'canal') v = porta.canal === r.valor;
    else if (r.campo === 'segue') {
      // Sem resposta da Meta (nulo), nenhuma das duas comparações vale: segue pela saída "não".
      const segue = await porta.segueConta();
      v = segue === null ? false : r.operador === 'segue' ? segue : !segue;
    }
    resultados.push(v);
    if (k.juncao === 'alguma' && v) return true;
    if (k.juncao === 'todas' && !v) return false;
  }
  return k.juncao === 'todas' ? resultados.every(Boolean) : resultados.some(Boolean);
}

const prazoEm = (agora: Date, horas: number | null | undefined) => (horas ? new Date(agora.getTime() + horas * 3_600_000).toISOString() : null);

/**
 * Roda o fluxo a partir da entrada. Devolve o estado novo e os passos desta rodada.
 * - Execução que espera e recebe outra coisa (mensagem quando espera tempo, clique de outra caixa) fica como está.
 * - Erro de envio (janela fechada, canal fora) encerra a execução como `falhou`, sem nova tentativa.
 * - Teto de passos (rodada e execução) encerra como `falhou` (laço).
 */
export async function rodarFluxo(desenho: Desenho, anterior: EstadoExecucao, entrada: EntradaFluxo, porta: PortaFluxo): Promise<RodadaFluxo> {
  const estado: EstadoExecucao = { ...anterior, variaveis: { ...anterior.variaveis } };
  const passos: PassoFluxo[] = [];
  if (estado.situacao !== 'rodando' && estado.situacao !== 'aguardando') return { estado, passos };
  const caixas = new Map(desenho.caixas.map((c) => [c.id, c]));
  const proxima = (de: string, saida: string) => desenho.ligacoes.find((l) => l.de === de && l.saida === saida)?.para ?? null;
  const registrar = (c: Caixa, resultado: PassoFluxo['resultado'], saida: string | null, detalhe: string | null, inicio: number) => {
    passos.push({ caixa: c.id, tipo: c.tipo, resultado, saida, detalhe: detalhe?.slice(0, 500) ?? null, duracao_ms: Math.max(0, Date.now() - inicio) });
  };
  const encerrar = (situacao: 'concluida' | 'falhou', motivo: string | null) => {
    estado.situacao = situacao;
    estado.motivo = motivo?.slice(0, 500) ?? null;
    estado.espera = null;
    return { estado, passos };
  };

  // 1. De onde começa (a caixa seguinte) conforme a entrada.
  let atual: string | null = null;
  /** Nova tentativa da espera `repetir`: recusada de novo, não grava outro passo (review F2-r1, ROB-M1). */
  let repetindo = false;
  if (entrada.tipo === 'inicio') {
    const gatilho = desenho.caixas.find((c) => GRUPO_CAIXA[c.tipo] === 'gatilho');
    if (!gatilho) return encerrar('falhou', MOTIVOS.semGatilho);
    registrar(gatilho, 'ok', 'proximo', null, Date.now());
    estado.passos++;
    estado.caixa = gatilho.id;
    atual = proxima(gatilho.id, 'proximo');
  } else if (entrada.tipo === 'retomar_saida') {
    if (!caixas.has(entrada.caixa)) return encerrar('falhou', MOTIVOS.caixaInexistente);
    estado.caixa = entrada.caixa;
    atual = proxima(entrada.caixa, entrada.saida);
  } else {
    const e = estado.espera;
    if (!e || estado.situacao !== 'aguardando') return { estado, passos };
    const c = caixas.get(e.caixa);
    if (!c) return encerrar('falhou', MOTIVOS.caixaInexistente);
    const inicio = Date.now();
    if (entrada.tipo === 'tempo') {
      if (!e.ate || porta.agora().getTime() < new Date(e.ate).getTime()) return { estado, passos };
      if (e.tipo === 'repetir') {
        // A própria caixa roda de novo (o registro sai na rodada dela).
        atual = c.id;
        repetindo = true;
      } else {
        const saida = e.tipo === 'tempo' ? 'proximo' : 'sem_resposta';
        registrar(c, 'ok', saida, e.tipo === 'tempo' ? DETALHES.esperaConcluida : DETALHES.semResposta, inicio);
        atual = proxima(c.id, saida);
      }
    } else if (entrada.tipo === 'webhook') {
      if (e.tipo !== 'webhook' || entrada.caixa !== e.caixa || c.tipo !== 'webhook') return { estado, passos };
      const saida = entrada.ok ? 'ok' : 'erro';
      registrar(c, entrada.ok ? 'ok' : 'erro', saida, entrada.detalhe, inicio);
      atual = proxima(c.id, saida);
    } else if (entrada.tipo === 'app_externo') {
      if (e.tipo !== 'app_externo' || entrada.caixa !== e.caixa || c.tipo !== 'app_externo') return { estado, passos };
      const novas = Object.keys(entrada.variaveis ?? {}).filter((k) => !(k in estado.variaveis));
      if (Object.keys(estado.variaveis).length + novas.length > LIMITES_FLUXO.variaveis) return encerrar('falhou', MOTIVOS.variaveisDemais);
      Object.assign(estado.variaveis, entrada.variaveis ?? {});
      const saida = entrada.ok ? 'ok' : 'erro';
      registrar(c, entrada.ok ? 'ok' : 'erro', saida, entrada.detalhe, inicio);
      atual = proxima(c.id, saida);
    } else if (entrada.tipo === 'clique') {
      if (e.tipo !== 'clique' || entrada.caixa !== e.caixa || (c.tipo !== 'botoes' && c.tipo !== 'resposta_privada') || !c.config.opcoes.some((o) => o.id === entrada.saida)) return { estado, passos };
      registrar(c, 'ok', entrada.saida, DETALHES.clique, inicio);
      atual = proxima(c.id, entrada.saida);
    } else if (entrada.tipo === 'mensagem') {
      if (e.tipo === 'clique' && (c.tipo === 'botoes' || c.tipo === 'resposta_privada')) {
        // Resposta escrita igual ao texto de uma opção vale como o clique (WhatsApp antigo, Instagram sem botão).
        const op = c.config.opcoes.find((o) => normalizarPalavra(o.titulo) === normalizarPalavra(entrada.texto));
        if (!op) return { estado, passos };
        registrar(c, 'ok', op.id, DETALHES.respostaOpcao, inicio);
        atual = proxima(c.id, op.id);
      } else if (e.tipo === 'resposta' && c.tipo === 'pergunta') {
        const valor = respostaValida(entrada.texto, c.config.formato);
        if (valor === null) {
          const aviso = c.config.mensagem_invalida?.trim() || 'Não entendi a resposta. Pode responder de novo?';
          const r = await porta.enviar({ caixa: c.id, texto: preencherTexto(aviso, estado.variaveis) });
          registrar(c, r.ok ? 'aguardando' : 'erro', null, r.ok ? DETALHES.respostaInvalida : r.erro, inicio);
          estado.passos++;
          if (!r.ok) return encerrar('falhou', r.erro);
          return { estado, passos };
        }
        if (Object.keys(estado.variaveis).length >= LIMITES_FLUXO.variaveis && !(c.config.variavel in estado.variaveis)) return encerrar('falhou', MOTIVOS.variaveisDemais);
        estado.variaveis[c.config.variavel] = valor;
        registrar(c, 'ok', 'respondeu', `${DETALHES.respostaGuardada}:${c.config.variavel}`, inicio);
        atual = proxima(c.id, 'respondeu');
      } else return { estado, passos };
    }
    if (e.tipo !== 'repetir') estado.passos++;
    estado.espera = null;
    estado.situacao = 'rodando';
  }

  // 2. Roda as caixas até esperar, terminar ou bater no teto.
  let naRodada = 0;
  while (atual) {
    const c = caixas.get(atual);
    if (!c) return encerrar('falhou', MOTIVOS.caixaInexistente);
    if (++naRodada > LIMITES_FLUXO.passosRodada || estado.passos >= LIMITES_FLUXO.passosExecucao) return encerrar('falhou', MOTIVOS.limitePassos);
    estado.passos++;
    estado.caixa = c.id;
    const inicio = Date.now();
    const agora = porta.agora();
    switch (c.tipo) {
      case 'gatilho_palavra':
      case 'gatilho_comentario':
      case 'gatilho_broadcast':
      case 'gatilho_app':
        return encerrar('falhou', MOTIVOS.ligacaoGatilho);
      case 'resposta_publica': {
        const r = await porta.responderComentario(c.id, preencherTexto(c.config.texto, estado.variaveis));
        registrar(c, r.ok ? 'ok' : 'erro', r.ok ? 'proximo' : null, r.ok ? DETALHES.respostaPublica : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        atual = proxima(c.id, 'proximo');
        break;
      }
      case 'resposta_privada': {
        const opcoes = c.config.opcoes ?? [];
        const r = await porta.respostaPrivada({
          caixa: c.id,
          texto: preencherTexto(c.config.texto, estado.variaveis),
          botoes: opcoes.length ? { modo: c.config.modo, opcoes: opcoes.map((o) => ({ saida: o.id, titulo: o.titulo })) } : null,
        });
        if (!r.ok && r.tentar_em) {
          // Teto por hora da conta: espera e tenta de novo (a regra dos 7 dias continua valendo na nova tentativa).
          if (!repetindo) registrar(c, 'aguardando', null, `${DETALHES.limiteHora}:${r.tentar_em}`, inicio);
          estado.espera = { tipo: 'repetir', caixa: c.id, ate: r.tentar_em };
          estado.situacao = 'aguardando';
          return { estado, passos };
        }
        if (!r.ok) {
          registrar(c, 'erro', null, r.erro, inicio);
          return encerrar('falhou', r.erro);
        }
        if (!opcoes.length) {
          registrar(c, 'ok', 'proximo', DETALHES.respostaPrivada, inicio);
          atual = proxima(c.id, 'proximo');
          break;
        }
        registrar(c, 'aguardando', null, DETALHES.esperandoClique, inicio);
        estado.espera = { tipo: 'clique', caixa: c.id, ate: prazoEm(agora, c.config.prazo_horas) };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'oportunidade': {
        const r = await porta.oportunidade(preencherTexto(c.config.titulo, estado.variaveis).trim().slice(0, LIMITES_FLUXO.tituloOportunidade), c.config.funil?.trim() || null);
        registrar(c, r.ok ? 'ok' : 'erro', r.ok ? 'proximo' : null, r.ok ? (r.detalhe ?? DETALHES.oportunidadeAberta) : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        atual = proxima(c.id, 'proximo');
        break;
      }
      case 'mensagem': {
        const r = await porta.enviar({ caixa: c.id, texto: preencherTexto(c.config.texto, estado.variaveis), midia: c.config.midia ?? null });
        registrar(c, r.ok ? 'ok' : 'erro', r.ok ? 'proximo' : null, r.ok ? (r.detalhe ?? null) : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        atual = proxima(c.id, 'proximo');
        break;
      }
      case 'botoes': {
        const r = await porta.enviar({
          caixa: c.id,
          texto: preencherTexto(c.config.texto, estado.variaveis),
          botoes: { modo: c.config.modo, opcoes: c.config.opcoes.map((o) => ({ saida: o.id, titulo: o.titulo })) },
        });
        registrar(c, r.ok ? 'aguardando' : 'erro', null, r.ok ? DETALHES.esperandoClique : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        estado.espera = { tipo: 'clique', caixa: c.id, ate: prazoEm(agora, c.config.prazo_horas) };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'pergunta': {
        const r = await porta.enviar({ caixa: c.id, texto: preencherTexto(c.config.texto, estado.variaveis) });
        registrar(c, r.ok ? 'aguardando' : 'erro', null, r.ok ? DETALHES.esperandoResposta : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        estado.espera = { tipo: 'resposta', caixa: c.id, ate: prazoEm(agora, c.config.prazo_horas) };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'condicao': {
        const sim = await avaliarCondicao(c.config, estado, porta);
        registrar(c, 'ok', sim ? 'sim' : 'nao', null, inicio);
        atual = proxima(c.id, sim ? 'sim' : 'nao');
        break;
      }
      case 'espera': {
        const ate = new Date(agora.getTime() + c.config.quantidade * MS_UNIDADE[c.config.unidade]).toISOString();
        registrar(c, 'aguardando', null, `${DETALHES.ate}:${ate}`, inicio);
        estado.espera = { tipo: 'tempo', caixa: c.id, ate };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'etiqueta': {
        const r = await porta.etiqueta(c.config.acao, c.config.etiqueta.trim());
        registrar(c, r.ok ? 'ok' : 'erro', r.ok ? 'proximo' : null, r.ok ? null : r.erro, inicio);
        if (!r.ok) return encerrar('falhou', r.erro);
        atual = proxima(c.id, 'proximo');
        break;
      }
      case 'transferir': {
        const msg = c.config.mensagem?.trim() ? preencherTexto(c.config.mensagem, estado.variaveis) : null;
        const r = await porta.transferir(c.config.fila_id, msg);
        registrar(c, r.ok ? 'ok' : 'erro', null, r.ok ? DETALHES.transferida : r.erro, inicio);
        return r.ok ? encerrar('concluida', MOTIVOS.transferida) : encerrar('falhou', r.erro);
      }
      case 'webhook': {
        // Review r65 (ROB-M1): o POST não roda aqui (dentro da transação do job, com a execução e a conversa travadas). A
        // rodada para e o motor chama fora da transação; o resultado volta como a entrada `webhook`.
        registrar(c, 'aguardando', null, DETALHES.webhook, inicio);
        estado.espera = { tipo: 'webhook', caixa: c.id, ate: null };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'app_externo': {
        // U14 F6: como o webhook, a chamada à Maton não roda aqui; o motor chama fora da transação e devolve a entrada.
        registrar(c, 'aguardando', null, DETALHES.appExterno, inicio);
        estado.espera = { tipo: 'app_externo', caixa: c.id, ate: null };
        estado.situacao = 'aguardando';
        return { estado, passos };
      }
      case 'fim':
        registrar(c, 'ok', null, null, inicio);
        return encerrar('concluida', null);
    }
  }
  return encerrar('concluida', null);
}
